import React, { useMemo, useState } from 'react';
import {
    buildPartnerRequestShopReadyReportText,
    getPartnerRequestShopReadyReportData,
} from '../../utils/partnerRequestDisplay';

const ReportIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="6" y="3" width="12" height="18" rx="2" />
        <path d="M9 7h6" />
        <path d="M9 11h6" />
        <path d="M9 15h4" />
    </svg>
);

const CopyIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
);

const SectionLabel = ({ children }) => (
    <p className="text-[10px] font-black uppercase tracking-[0.09em] text-[#757575]">{children}</p>
);

const BulletList = ({ items = [], emptyText = '' }) => {
    if (!items.length) {
        return emptyText ? <p className="mt-2 text-sm font-semibold text-[#414141]">{emptyText}</p> : null;
    }
    return (
        <ul className="mt-2 space-y-1.5 text-sm font-semibold leading-relaxed text-[#414141]">
            {items.map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#414141]" />
                    <span>{item}</span>
                </li>
            ))}
        </ul>
    );
};

const NumberedList = ({ items = [], emptyText = '' }) => {
    if (!items.length) {
        return emptyText ? <p className="mt-2 text-sm font-semibold text-[#414141]">{emptyText}</p> : null;
    }
    return (
        <ol className="mt-2 space-y-1.5 text-sm font-semibold leading-relaxed text-[#414141]">
            {items.map((item, index) => (
                <li key={item} className="flex items-start gap-2.5">
                    <span className="font-black text-[#414141]">{index + 1}.</span>
                    <span>{item}</span>
                </li>
            ))}
        </ol>
    );
};

const EvidenceGrid = ({ items = [] }) => {
    if (!items.length) return null;
    return (
        <div className="mt-3 flex flex-wrap gap-2.5">
            {items.map((media, index) => (
                <a
                    key={media.id || `${media.label}-${index}`}
                    href={media.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={media.label}
                    aria-label={media.label}
                    className="group block h-20 w-20 overflow-hidden rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] text-[#414141]"
                >
                    {media.isImage ? (
                        <img src={media.url} alt={media.label} loading="lazy" className="h-full w-full object-cover transition group-hover:opacity-90" />
                    ) : (
                        <span className="flex h-full w-full flex-col items-center justify-center gap-1 px-2 text-center">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                {media.isVideo
                                    ? (<><rect x="2" y="5" width="14" height="14" rx="2" /><path d="m22 8-6 4 6 4V8Z" /></>)
                                    : (<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></>)}
                            </svg>
                            <span className="w-full truncate text-[10px] font-bold">{media.isVideo ? 'Video' : 'File'}</span>
                        </span>
                    )}
                </a>
            ))}
        </div>
    );
};

export default function ShopReadyReportCard({ request, className = '' }) {
    const [copyNotice, setCopyNotice] = useState('');
    const report = useMemo(() => getPartnerRequestShopReadyReportData(request), [request]);
    const summaryText = useMemo(() => buildPartnerRequestShopReadyReportText(request), [request]);

    const handleCopy = async () => {
        try {
            if (!navigator?.clipboard?.writeText) {
                setCopyNotice('Clipboard unavailable');
            } else {
                await navigator.clipboard.writeText(summaryText);
                setCopyNotice('Summary copied');
            }
        } catch (_) {
            setCopyNotice('Unable to copy');
        }
        window.setTimeout(() => setCopyNotice(''), 1800);
    };

    return (
        <div className={`w-full overflow-hidden rounded-2xl border border-[#d9d9d9] bg-white text-[#414141] shadow-sm ${className}`}>
            <div className="flex items-start gap-3 px-4 py-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#f5f5f5] text-[#414141]">
                    <ReportIcon />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                            <p className="text-base font-black leading-tight text-[#414141]">Shop-Ready Report</p>
                            <p className="mt-1 text-sm font-semibold leading-relaxed text-[#414141]">
                                Symptoms, likely causes, evidence, and recommended checks
                            </p>
                        </div>
                    </div>

                    <div className="mt-4 space-y-4">
                        <div>
                            <SectionLabel>Customer Complaint</SectionLabel>
                            <p className="mt-1.5 text-sm font-black leading-relaxed text-[#414141]">{report.customerComplaint}</p>
                        </div>

                        {report.shopReadySummary && report.shopReadySummary !== report.customerComplaint && (
                            <div>
                                <SectionLabel>Shop-Ready Summary</SectionLabel>
                                <p className="mt-1.5 text-sm font-semibold leading-relaxed text-[#414141]">{report.shopReadySummary}</p>
                            </div>
                        )}

                        <div className="grid gap-4 md:grid-cols-3">
                            <div>
                                <SectionLabel>Observed Symptoms</SectionLabel>
                                <BulletList items={report.observedSymptoms} emptyText="No observed symptoms captured." />
                            </div>
                            <div>
                                <SectionLabel>Likely Causes</SectionLabel>
                                <NumberedList items={report.likelyCauses} emptyText="Pending shop review." />
                            </div>
                            <div>
                                <SectionLabel>Recommended Checks</SectionLabel>
                                <BulletList items={report.recommendedChecks} emptyText="Inspect and confirm root cause." />
                            </div>
                        </div>

                        {report.completedDiagnosticChecks.length > 0 && (
                            <div>
                                <SectionLabel>Completed Diagnostic Checks</SectionLabel>
                                <div className="mt-2 space-y-2">
                                    {report.completedDiagnosticChecks.map((check, index) => (
                                        <div key={`${check.recommendedCheck}-${index}`} className="rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 py-3">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <p className="text-sm font-black text-[#414141]">{check.recommendedCheck}</p>
                                                {check.outcomeLabel && (
                                                    <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-black text-[#757575]">{check.outcomeLabel}</span>
                                                )}
                                            </div>
                                            {check.summary && <p className="mt-1.5 text-sm font-semibold leading-relaxed text-[#414141]">{check.summary}</p>}
                                            {check.notes && <p className="mt-1 text-sm font-semibold leading-relaxed text-[#414141]">Notes: {check.notes}</p>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {report.evidenceMedia.length > 0 && (
                            <div>
                                <SectionLabel>Evidence</SectionLabel>
                                <EvidenceGrid items={report.evidenceMedia} />
                            </div>
                        )}
                    </div>

                    <div className="mt-4 flex flex-col gap-2 border-t border-[#d9d9d9] pt-3 sm:flex-row sm:items-center">
                        <button
                            type="button"
                            onClick={handleCopy}
                            className="inline-flex min-h-[36px] items-center justify-center gap-2 rounded-xl border border-[#d9d9d9] bg-white px-3 text-xs font-black text-[#414141] transition hover:bg-[#F9F9F9]"
                        >
                            <CopyIcon />
                            Copy Summary
                        </button>
                        {copyNotice && <p className="text-xs font-black text-[#0a8f53]">{copyNotice}</p>}
                    </div>
                </div>
            </div>
        </div>
    );
}
